import React, { useState, useEffect } from 'react';
import moment from 'moment';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { ChevronLeftIcon, ChevronRightIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';

import API_URL from '../config/api';

const Calendar = () => {
  const { user } = useAuth();
  const [currentDate, setCurrentDate] = useState(moment());
  const [serviceCalls, setServiceCalls] = useState([]);  // Initialize as empty array
  const [timeOffRequests, setTimeOffRequests] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDateCalls, setSelectedDateCalls] = useState([]);
  const [selectedDateTimeOff, setSelectedDateTimeOff] = useState([]);
  const [showDayModal, setShowDayModal] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCalendarData();
  }, [currentDate]);

  const fetchCalendarData = async () => {
    try {
      const token = localStorage.getItem('token');
      const startDate = currentDate.clone().startOf('month').subtract(7, 'days').format('YYYY-MM-DD');
      const endDate = currentDate.clone().endOf('month').add(7, 'days').format('YYYY-MM-DD');
      
      // Add viewAll=true to allow employees to see all service calls on calendar
      const [serviceCallsRes, timeOffRes] = await Promise.all([
        fetch(
          `${API_URL}/api/service-calls?startDate=${startDate}&endDate=${endDate}&viewAll=true`,
          { headers: { 'Authorization': `Bearer ${token}` }}
        ),
        axios.get(`${API_URL}/api/hr/time-off/calendar?start_date=${startDate}&end_date=${endDate}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      if (serviceCallsRes.ok) {
        const data = await serviceCallsRes.json();
        setServiceCalls(Array.isArray(data.serviceCalls) ? data.serviceCalls : []);  // Ensure it's an array
      } else {
        setServiceCalls([]);  // Set to empty array on error
      }
      
      setTimeOffRequests(Array.isArray(timeOffRes.data) ? timeOffRes.data : []);  // Ensure it's an array
    } catch (error) {
      console.error('Error fetching calendar data:', error);
      setServiceCalls([]);  // Set to empty array on error
      setTimeOffRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = () => {
    const startDay = currentDate.clone().startOf('month').startOf('week');
    const endDay = currentDate.clone().endOf('month').endOf('week');
    const days = [];
    const day = startDay.clone();
    
    while (day <= endDay) {
      days.push(day.clone());
      day.add(1, 'day');
    }
    
    return days;
  };

  // Helper to format database dates without timezone shift
  const formatDbDate = (dateStr) => {
    return moment.utc(dateStr).format('YYYY-MM-DD');
  };

  const getCallsForDate = (date) => {
    // Defensive check to ensure serviceCalls is an array
    if (!Array.isArray(serviceCalls)) {
      return [];
    }
    const dateStr = date.format('YYYY-MM-DD');
    return serviceCalls.filter(call => formatDbDate(call.scheduled_date) === dateStr);
  };

  const getTimeOffForDate = (date) => {
    const dateStr = date.format('YYYY-MM-DD');
    // Defensive check to ensure timeOffRequests is an array
    if (!Array.isArray(timeOffRequests)) {
      return [];
    }
    return timeOffRequests.filter(request => {
      const start = formatDbDate(request.start_date);
      const end = formatDbDate(request.end_date);
      return dateStr >= start && dateStr <= end;
    });
  };

  const handleDateClick = (date) => {
    const calls = getCallsForDate(date);
    const timeOff = getTimeOffForDate(date);
    setSelectedDate(date);
    setSelectedDateCalls(calls);
    setSelectedDateTimeOff(timeOff);
    setShowDayModal(true);
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => prev.clone().add(direction, 'month'));
  };

  const goToToday = () => {
    setCurrentDate(moment());
  };

  const openNavigation = (address) => {
    if (!address) {
      toast.warning('No address available');
      return;
    }
    const encodedAddress = encodeURIComponent(address);
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`, '_blank');
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'completed': return '#10b981';
      case 'in-progress': return '#3b82f6';
      case 'pending': return '#f59e0b';
      case 'cancelled': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const days = getDaysInMonth();
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  if (loading) return <div className="spinner"></div>;

  return (
    <div>
      <div className="page-header" style={{ marginBottom: 24 }}>
        <h1 className="page-title" style={{ fontSize: 28, fontWeight: 600 }}>Calendar</h1>
      </div>

      {/* Calendar Controls */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-body" style={{ padding: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <button
                onClick={() => navigateMonth(-1)}
                style={{ 
                  background: 'white', 
                  border: '1px solid #e2e8f0', 
                  borderRadius: 6, 
                  padding: '8px 12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <ChevronLeftIcon style={{ width: 18, height: 18 }} />
              </button>
              
              <h2 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>
                {currentDate.format('MMMM YYYY')}
              </h2>
              
              <button
                onClick={() => navigateMonth(1)}
                style={{ 
                  background: 'white', 
                  border: '1px solid #e2e8f0', 
                  borderRadius: 6, 
                  padding: '8px 12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <ChevronRightIcon style={{ width: 18, height: 18 }} />
              </button>
            </div>

            <button
              onClick={goToToday}
              style={{ 
                background: '#2B7FE0', 
                color: 'white', 
                padding: '8px 16px', 
                borderRadius: 6, 
                border: 'none',
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 500
              }}
            >
              Today
            </button>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="card">
        <div className="card-body">
          {/* Weekday Headers */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 8 }}>
            {weekDays.map(day => (
              <div key={day} style={{ 
                padding: '12px 8px', 
                textAlign: 'center',
                fontWeight: 600,
                fontSize: 14,
                color: '#4a5568',
                borderBottom: '2px solid #e2e8f0'
              }}>
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
            {days.map((day, index) => {
              const calls = getCallsForDate(day);
              const timeOff = getTimeOffForDate(day);
              const isToday = day.isSame(moment(), 'day');
              const isCurrentMonth = day.isSame(currentDate, 'month');
              const myCallsCount = calls.filter(c => 
                c.assigned_to === user?.id || c.assigned_to_name === user?.name ||
                (c.assigned_employees && c.assigned_employees.some(e => e.user_id === user?.id || e.name === user?.name))
              ).length;
              
              return (
                <div
                  key={index}
                  onClick={() => handleDateClick(day)}
                  style={{ 
                    minHeight: 100,
                    padding: 8,
                    border: '1px solid #e2e8f0',
                    background: isToday ? '#fef3c7' : (isCurrentMonth ? 'white' : '#f9fafb'),
                    cursor: 'pointer',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                  onMouseLeave={(e) => e.currentTarget.style.background = isToday ? '#fef3c7' : (isCurrentMonth ? 'white' : '#f9fafb')}
                >
                  <div style={{ 
                    fontWeight: isToday ? 600 : 400,
                    color: isCurrentMonth ? '#1f2937' : '#9ca3af',
                    marginBottom: 4,
                    fontSize: 14
                  }}>
                    {day.format('D')}
                  </div>
                  
                  {/* Service Calls */}
                  {calls.length > 0 && (
                    <div>
                      <div style={{ 
                        fontSize: 11, 
                        padding: '2px 6px',
                        background: '#e0e7ff',
                        borderRadius: 4,
                        marginBottom: 4,
                        color: '#3730a3',
                        fontWeight: 500
                      }}>
                        {calls.length} {calls.length === 1 ? 'call' : 'calls'}
                      </div>
                      
                      {myCallsCount > 0 && (
                        <div style={{ 
                          fontSize: 11, 
                          padding: '2px 6px',
                          background: '#fbbf24',
                          borderRadius: 4,
                          color: '#78350f',
                          fontWeight: 500,
                          marginBottom: 4
                        }}>
                          {myCallsCount} assigned
                        </div>
                      )}
                      
                      {/* Show dots for different statuses */}
                      <div style={{ display: 'flex', gap: 2, marginTop: 4, flexWrap: 'wrap' }}>
                        {calls.slice(0, 3).map((call, i) => (
                          <div
                            key={i}
                            style={{ 
                              width: 8, 
                              height: 8, 
                              borderRadius: '50%',
                              background: getStatusColor(call.status)
                            }}
                          />
                        ))}
                        {calls.length > 3 && (
                          <span style={{ fontSize: 10, color: '#6b7280' }}>+{calls.length - 3}</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Time Off */}
                  {timeOff.length > 0 && (
                    <div style={{ marginTop: 4 }}>
                      <div style={{ 
                        fontSize: 11, 
                        padding: '2px 6px',
                        background: '#ddd6fe',
                        borderRadius: 4,
                        color: '#5b21b6',
                        fontWeight: 500
                      }}>
                        🏖️ {timeOff.length} out
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div style={{ marginTop: 20, padding: 16, background: '#f9fafb', borderRadius: 8 }}>
            <p style={{ fontSize: 12, fontWeight: 500, marginBottom: 8, color: '#4a5568' }}>INDICATORS:</p>
            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
              {[
                { status: 'pending', label: 'Pending' },
                { status: 'in-progress', label: 'In Progress' },
                { status: 'completed', label: 'Completed' },
                { status: 'cancelled', label: 'Cancelled' }
              ].map(item => (
                <div key={item.status} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ 
                    width: 10, 
                    height: 10, 
                    borderRadius: '50%',
                    background: getStatusColor(item.status)
                  }} />
                  <span style={{ fontSize: 12, color: '#6b7280' }}>{item.label}</span>
                </div>
              ))}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ 
                  width: 10, 
                  height: 10, 
                  background: '#ddd6fe',
                  borderRadius: 2
                }} />
                <span style={{ fontSize: 12, color: '#6b7280' }}>Time Off</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Day Details Modal */}
      {showDayModal && (
        <div 
          style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            background: 'rgba(0,0,0,0.5)', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            zIndex: 1000 
          }}
          onClick={() => setShowDayModal(false)}
        >
          <div 
            style={{ 
              background: 'white', 
              borderRadius: 8, 
              width: '90%', 
              maxWidth: 600,
              maxHeight: '80vh',
              overflowY: 'auto'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ 
              padding: 20, 
              borderBottom: '1px solid #e2e8f0',
              position: 'sticky',
              top: 0,
              background: 'white'
            }}>
              <h2 style={{ fontSize: 20, fontWeight: 600 }}>
                {selectedDate?.format('dddd, MMMM D, YYYY')}
              </h2>
              <p style={{ color: '#718096', fontSize: 14, marginTop: 4 }}>
                {selectedDateCalls.length} service {selectedDateCalls.length === 1 ? 'call' : 'calls'}
                {selectedDateTimeOff.length > 0 && ` • ${selectedDateTimeOff.length} employee${selectedDateTimeOff.length === 1 ? '' : 's'} out`}
              </p>
            </div>
            
            <div style={{ padding: 20 }}>
              {/* Time Off Section */}
              {selectedDateTimeOff.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12, color: '#5b21b6' }}>🏖️ Time Off</h3>
                  {selectedDateTimeOff.map(request => (
                    <div key={request.id} style={{ 
                      padding: 12, 
                      background: '#f3e8ff',
                      borderRadius: 6, 
                      marginBottom: 8,
                      border: '1px solid #ddd6fe'
                    }}>
                      <div style={{ fontWeight: 600, marginBottom: 4, color: '#5b21b6' }}>
                        {request.user_name} - {request.request_type}
                      </div>
                      <div style={{ fontSize: 12, color: '#6b21a8' }}>
                        {moment.utc(request.start_date).format('MMM D')} - {moment.utc(request.end_date).format('MMM D')} 
                        ({request.days_count} {request.days_count === 1 ? 'day' : 'days'})
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Service Calls Section */}
              {selectedDateCalls.length === 0 && selectedDateTimeOff.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#718096' }}>No events scheduled for this date</p>
              ) : selectedDateCalls.length > 0 ? (
                <div>
                  <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Service Calls</h3>
                  {selectedDateCalls.map(call => {
                    // Check if current user is assigned
                    const isAssignedToMe = call.assigned_to === user?.id || 
                      call.assigned_to_name === user?.name ||
                      (call.assigned_employees && call.assigned_employees.some(e => e.user_id === user?.id || e.name === user?.name));
                    
                    return (
                      <div key={call.id} style={{ 
                        padding: 16, 
                        background: isAssignedToMe ? '#fef3c7' : '#f7fafc',
                        borderRadius: 8, 
                        marginBottom: 12,
                        border: isAssignedToMe ? '2px solid #fbbf24' : '1px solid #e2e8f0'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 8 }}>
                          <div>
                            <h3 style={{ fontWeight: 600, marginBottom: 4 }}>
                              {call.scheduled_time || 'TBD'} - {call.title}
                            </h3>
                            <p style={{ color: '#4a5568' }}>{call.client_name}</p>
                            <p style={{ color: '#718096', fontSize: 14 }}>{call.client_address}</p>
                          </div>
                          <span style={{ 
                            padding: '4px 8px',
                            borderRadius: 4,
                            fontSize: 12,
                            fontWeight: 500,
                            background: getStatusColor(call.status),
                            color: 'white'
                          }}>
                            {call.status}
                          </span>
                        </div>
                        
                        {call.description && (
                          <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>{call.description}</p>
                        )}
                        
                        {/* Show assigned employees */}
                        {call.assigned_employees && call.assigned_employees.length > 0 ? (
                          <p style={{ fontSize: 13, color: '#4a5568', marginBottom: 8 }}>
                            Assigned to: <strong>{call.assigned_employees.map(e => e.name).join(', ')}</strong>
                          </p>
                        ) : call.assigned_to_name && (
                          <p style={{ fontSize: 13, color: '#4a5568', marginBottom: 8 }}>
                            Assigned to: <strong>{call.assigned_to_name}</strong>
                          </p>
                        )}
                        
                        <button 
                          onClick={() => openNavigation(call.client_address)}
                          style={{ 
                            padding: '6px 12px', 
                            borderRadius: 4,
                            border: '1px solid #e2e8f0',
                            background: 'white',
                            fontSize: 13,
                            cursor: 'pointer'
                          }}
                        >
                          📍 Navigate
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calendar;
